import { useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from "react";

import { Icon } from "@/components/Icon";
import { sendAgentMessage } from "@/components/agent/agentClient";
import { renderMarkdown } from "@/components/agent/markdown";
import {
  appendMessage,
  loadState,
  resetState,
  type AgentState,
} from "@/lib/agentHistory";

interface Props {
  onClose: () => void;
}

const QUICK_PROMPTS = [
  "¿Cuántos mountpoints hay activos?",
  "Lista los clientes conectados ahora",
  "Explícame qué es un mountpoint",
];

export function AgentPanel({ onClose }: Props) {
  const [state, setState] = useState<AgentState>(() => loadState());
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const messages = state.messages;
  const showQuick = useMemo(() => messages.length === 0, [messages.length]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, sending]);

  const submit = async (raw?: string) => {
    const text = (raw ?? input).trim();
    if (!text || sending) return;
    setInput("");
    setError(null);

    let next = appendMessage(state, "user", text);
    setState(next);
    setSending(true);

    try {
      const res = await sendAgentMessage(next.sessionId, text);
      next = appendMessage(next, "assistant", res.reply);
      setState(next);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Error desconocido";
      setError(msg);
      next = appendMessage(next, "assistant", `⚠️ ${msg}`);
      setState(next);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit();
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const onReset = () => {
    if (sending) return;
    setState(resetState());
    setError(null);
  };

  return (
    <div className="agent-panel card--ticks" role="dialog" aria-label="Asistente Casper">
      <header className="agent-panel__head">
        <div className="agent-panel__title">
          <span className="agent-panel__avatar">
            <Icon.Bot />
          </span>
          <div>
            <b>Casper</b>
            <span className="agent-panel__sub">Asistente del Caster</span>
          </div>
        </div>
        <div className="row" style={{ gap: 4 }}>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onReset}
            title="Nueva conversación"
            aria-label="Reiniciar conversación"
            disabled={sending}
            style={{ padding: 6 }}
          >
            <Icon.Trash />
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onClose}
            aria-label="Cerrar"
            style={{ padding: 6 }}
          >
            <Icon.X />
          </button>
        </div>
      </header>

      <div className="agent-panel__body" ref={scrollRef}>
        {messages.length === 0 && (
          <div className="agent-panel__welcome">
            <div className="agent-panel__welcome-title">¿En qué te ayudo?</div>
            <p>
              Puedo listar mountpoints, clientes y sesiones, explicar conceptos NTRIP
              y crear recursos (con confirmación). No puedo deshabilitar ni borrar nada.
            </p>
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={`${m.ts}-${i}`}
            className={`agent-msg agent-msg--${m.role}`}
          >
            {m.role === "assistant" ? (
              <div
                className="agent-msg__bubble"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content) }}
              />
            ) : (
              <div className="agent-msg__bubble">{m.content}</div>
            )}
          </div>
        ))}
        {sending && (
          <div className="agent-msg agent-msg--assistant">
            <div className="agent-msg__bubble agent-typing">
              <span />
              <span />
              <span />
            </div>
          </div>
        )}
      </div>

      {showQuick && (
        <div className="agent-panel__quick">
          {QUICK_PROMPTS.map((q) => (
            <button
              key={q}
              type="button"
              className="agent-panel__chip"
              onClick={() => submit(q)}
              disabled={sending}
            >
              {q}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div className="agent-panel__error" role="alert">
          {error}
        </div>
      )}

      <form className="agent-panel__foot" onSubmit={onSubmit}>
        <textarea
          ref={inputRef}
          className="agent-input"
          rows={1}
          placeholder="Escríbele a Casper… (Enter envía, Shift+Enter salto)"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          disabled={sending}
        />
        <button
          type="submit"
          className="btn btn--accent"
          disabled={sending || !input.trim()}
          aria-label="Enviar"
          style={{ padding: "8px 12px" }}
        >
          {sending ? <span className="spinner" /> : <Icon.Send />}
        </button>
      </form>
    </div>
  );
}
